import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '../environments/environment';

type AuthenticationResponse = {
  token: string;
};

/** Site versions, keyed by the profile's paxTypeNo (1 = VIP, 2 = GSCS, 3 = EDA). */
export type SiteVersion = 'VIP' | 'GSCS' | 'EDA';

const paxTypeToVersion: Record<number, SiteVersion> = {
  1: 'VIP',
  2: 'GSCS',
  3: 'EDA',
};

const tokenStorageKey = 'mtc-session-token';
const usernameStorageKey = 'mtc-username';
const profileStorageKey = 'mtc-profile';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly http = inject(HttpClient);

  private readonly profileState = signal<any>(this.readProfile());

  /** Which version of the site the logged-in user sees; null if the pax type isn't 1, 2 or 3. */
  readonly paxVersion = computed<SiteVersion | null>(
    () => paxTypeToVersion[Number(this.profileState().paxTypeNo)] ?? null,
  );

  persist(username: string, token: string, profile: any): void {
    sessionStorage.setItem(usernameStorageKey, username);
    sessionStorage.setItem(tokenStorageKey, token);
    sessionStorage.setItem(profileStorageKey, JSON.stringify(profile));
    this.profileState.set(profile ?? {});
  }

  getProfile():any{
    return this.readProfile();
  }

  clear(): void {
    sessionStorage.removeItem(usernameStorageKey);
    sessionStorage.removeItem(tokenStorageKey);
    sessionStorage.removeItem(profileStorageKey);
    this.profileState.set({});
  }

  private readProfile(): any {
    return JSON.parse(sessionStorage.getItem(profileStorageKey) || '{}');
  }

  validate() {
    const username = sessionStorage.getItem(usernameStorageKey);
    const token = sessionStorage.getItem(tokenStorageKey);

    if (!username || !token) {
      return null;
    }

    return this.http.get<AuthenticationResponse>(`${environment.authUrl}mtc-session-token`, {
      headers: {
        programkey: environment.programkey,
        'mtc-session-token': token,
      },
    });
  }
}
